import { addDays, isBefore } from 'date-fns';
import { EMPTY, catchError, combineLatest, from, merge, of, startWith, take } from 'rxjs';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { createSaveTableSettingsEpic } from '../../../common/datagrid';
import { notifyPriceActivated } from '../../../common/signalr/signalrActions';
import { RootEpic } from '../../../store';
import {
    createEpicFromAsyncAction,
    distinct,
    getAllPricelistChildren,
    notNull,
    toDateString
} from '../../../utils';
import { fetchPricelists } from '../../pricelist/redux/pricelistSlices';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { fetchStocksForDay } from '../../stock/redux/stockSlices';
import { pricePrgError } from '../../ui/redux/uiSlices';
import {
    activatePrice,
    fetchInactivatePrices,
    fetchInactivatePricesPartial,
    getInactivatePriceKey,
    inactivatePricesTableActions,
    inactivatePricesTableFetchPricelistPeriodValidFroms,
    inactivatePricesTablePricelistDateSelected,
    inactivatePricesTablePricelistIdsSelected,
    inactivatePricesTableWarehouseDateSelected,
    inactivatePricesTableWarehouseSelected,
    setPriceActiveStatusOptimistically
} from './inactivatePricesSlices';

const fetchExternalPricelistPeriods$ = createEpicFromAsyncAction(
    inactivatePricesTableFetchPricelistPeriodValidFroms,
    async (payload, { pricelistClient }) => {
        const plps = await pricelistClient.getPricelistsPeriodsForPricelists(payload.pricelistIds);

        return {
            preselectDate: payload.preselectDate,
            validFroms: plps.map(plp => plp.validFrom).filter(distinct)
        };
    }
);

const fetchInactivatePrices$ = createEpicFromAsyncAction(fetchInactivatePrices, (payload, { priceClient }) =>
    priceClient.getPricesByValidFrom(payload.pricelistIds, payload.validFrom)
);

const fetchInactivatePricesPartial$ = createEpicFromAsyncAction(
    fetchInactivatePricesPartial,
    (payload, { priceClient }) =>
        priceClient.getPricesByValidFromPartial({
            pricelistIds: payload.pricelistIds,
            productIds: payload.productIds,
            validAt: payload.validFrom
        })
);

const pricelistDateSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(inactivatePricesTablePricelistDateSelected.match),
        withLatestFrom(state$.pipe(map(state => state.inactivatePrices.prices.selectedPricelistIds))),
        filter(([, pricelistIds]) => pricelistIds.length > 0),
        map(([action, pricelistIds]) =>
            fetchInactivatePrices.request({
                pricelistIds: pricelistIds,
                validFrom: action.payload
            })
        )
    );

const pricelistsSelected$: RootEpic = action$ =>
    action$.pipe(
        filter(inactivatePricesTablePricelistIdsSelected.match),
        filter(action => action.payload.length > 0),
        map(action =>
            inactivatePricesTableFetchPricelistPeriodValidFroms.request({
                preselectDate: true,
                pricelistIds: action.payload
            })
        )
    );

const activatePrice$: RootEpic = (action$, state$, { priceClient }) =>
    action$.pipe(
        filter(activatePrice.request.match),
        withLatestFrom(
            state$.pipe(map(state => state.pricelist.pricelists)),
            state$.pipe(map(state => state.inactivatePrices.prices.prices))
        ),
        mergeMap(([action, pricelists, prices]) => {
            const {
                payload: { pricelistId, productId, isActive }
            } = action;

            const priceId = prices[getInactivatePriceKey({ pricelistId, productId })]?.priceId;

            if (priceId == null) {
                return EMPTY;
            }

            const priceIds = getAllPricelistChildren(pricelistId, Object.values(pricelists))
                .map(plId => prices[getInactivatePriceKey({ pricelistId: plId, productId })]?.priceId)
                .filter(notNull);

            return from(priceClient.activatePrice(priceId, isActive)).pipe(
                mergeMap(() => [
                    activatePrice.success([priceId, ...priceIds]),
                    notifyPriceActivated(priceId)
                ]),
                catchError(() => [
                    activatePrice.failure([priceId, ...priceIds]),
                    pricePrgError('Failed to update price')
                ]),
                startWith(
                    setPriceActiveStatusOptimistically({
                        isActive: action.payload.isActive,
                        rootPriceId: priceId,
                        priceIdChildren: priceIds
                    })
                )
            );
        })
    );

const inactivatePriceTable$ = createSaveTableSettingsEpic(
    inactivatePricesTableActions,
    state => state.inactivatePrices.ui.table,
    [inactivatePricesTablePricelistIdsSelected]
);

const setDefaultDateWhenPricelistsHasBeenFetched$: RootEpic = action$ =>
    action$.pipe(
        filter(inactivatePricesTableFetchPricelistPeriodValidFroms.success.match),
        filter(a => a.payload.preselectDate),
        mergeMap(action => {
            const now = addDays(new Date(), 1);

            // Automatically select the closest past date from tomorrow (inclusive)
            // where a pricelistperiod valid from exists
            const selectedDate = action.payload.validFroms
                .map(c => new Date(c))
                .reduce((selDate, currDate) => {
                    if (selDate == null) {
                        return currDate;
                    }

                    if (isBefore(selDate, currDate) && isBefore(currDate, now)) {
                        return currDate;
                    }

                    return selDate;
                }, undefined as Date | undefined);

            if (selectedDate == null) {
                return EMPTY;
            }

            return of(inactivatePricesTablePricelistDateSelected(toDateString(selectedDate)));
        })
    );

const inactivatePriceTableSettingsLoadedFetchValidFroms$: RootEpic = (action$, state$) =>
    // Use combine latest in order to not proceed with selecting columns until
    // ext pricelists and pricelists has been fetched.
    combineLatest([
        action$.pipe(filter(fetchUserSettings.success.match)),
        action$.pipe(filter(fetchPricelists.success.match), take(1))
    ]).pipe(
        withLatestFrom(state$.pipe(map(state => state.inactivatePrices.prices.selectedPricelistIds))),
        filter(([, selectedExtPricelistIds]) => selectedExtPricelistIds.length > 0),
        map(([, selectedExtPricelistIds]) =>
            inactivatePricesTablePricelistIdsSelected(selectedExtPricelistIds)
        )
    );

const fetchWarehouseBalance$: RootEpic = (action$, state$) =>
    merge(
        action$.pipe(
            filter(inactivatePricesTableWarehouseSelected.match),
            filter(action => action.payload != null),
            withLatestFrom(state$.pipe(map(state => state.agreement.ui.stockDate))),
            map(([action, selectedDate]) =>
                fetchStocksForDay.request({
                    date: selectedDate,
                    warehouseId: action.payload!
                })
            )
        ),
        action$.pipe(
            filter(inactivatePricesTableWarehouseDateSelected.match),
            withLatestFrom(state$.pipe(map(state => state.agreement.ui.warehouseId))),
            filter(([, warehouse]) => warehouse != null),
            map(([action, warehouse]) =>
                fetchStocksForDay.request({
                    date: action.payload,
                    warehouseId: warehouse!
                })
            )
        )
    );

export default [
    fetchExternalPricelistPeriods$,
    fetchInactivatePrices$,
    fetchInactivatePricesPartial$,
    pricelistDateSelected$,
    pricelistsSelected$,
    activatePrice$,
    inactivatePriceTable$,
    setDefaultDateWhenPricelistsHasBeenFetched$,
    inactivatePriceTableSettingsLoadedFetchValidFroms$,
    fetchWarehouseBalance$
];
